/**
 * Unified Animation Manager
 *
 * Coordinates multiple particle systems in a single requestAnimationFrame loop
 * to prevent competing animation loops and improve performance.
 */

type ParticleSystem = {
  id: string;
  particles: any[];
  blobOffset: { x: number; y: number };
  settings: any;
  context: CanvasRenderingContext2D;
  canvas: HTMLCanvasElement;
  isActive: boolean;
  mapRange: (
    in_min: number,
    in_max: number,
    out_min: number,
    out_max: number,
    current: number,
  ) => number;
};

class AnimationManager {
  private static instance: AnimationManager | null = null;
  private particleSystems: Map<string, ParticleSystem> = new Map();
  private animationId: number | null = null;
  private isRunning = false;

  private constructor() {}

  static getInstance(): AnimationManager {
    if (!AnimationManager.instance) {
      AnimationManager.instance = new AnimationManager();
    }
    return AnimationManager.instance;
  }

  registerParticleSystem(system: ParticleSystem): void {
    this.particleSystems.set(system.id, system);

    // Start animation loop if not already running
    if (!this.isRunning) {
      this.startAnimationLoop();
    }
  }

  unregisterParticleSystem(id: string): void {
    this.particleSystems.delete(id);

    // Stop animation loop if no systems are registered
    if (this.particleSystems.size === 0 && this.isRunning) {
      this.stopAnimationLoop();
    }
  }

  setSystemActive(id: string, active: boolean): void {
    const system = this.particleSystems.get(id);
    if (system) {
      system.isActive = active;
    }
  }

  private startAnimationLoop(): void {
    if (this.isRunning) return;

    this.isRunning = true;

    const animate = () => {
      // Update all active particle systems
      this.particleSystems.forEach((system) => {
        if (!system.isActive || !system.context || !system.canvas) return;

        const { particles, blobOffset, settings, context, canvas, mapRange } =
          system;

        // Update blob offset
        blobOffset.x += settings.blobSpeed;
        blobOffset.y += settings.blobSpeed;

        // Clear canvas
        context.clearRect(0, 0, canvas.width, canvas.height);

        // Apply transformations
        context.save();

        // Particles are laid out in `settings.width` units. A system may give
        // its canvas a larger backing store for sharper dots on high-density
        // screens, so scale the layout up to fill it.
        const ratio = canvas.width / settings.width;
        context.scale(ratio, ratio);

        const x = mapRange(1, 3, 0, settings.width, settings.scale);
        const y = mapRange(1, 3, 0, settings.height, settings.scale);
        context.translate(x, y);
        context.scale(settings.scale, settings.scale);

        // Update and draw particles
        particles.forEach((particle) => {
          particle.Move(blobOffset);
        });

        context.restore();
      });

      if (this.isRunning) {
        this.animationId = requestAnimationFrame(animate);
      }
    };

    this.animationId = requestAnimationFrame(animate);
  }

  private stopAnimationLoop(): void {
    this.isRunning = false;

    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  // Force stop all animations (useful for cleanup)
  stopAll(): void {
    this.particleSystems.clear();
    this.stopAnimationLoop();
  }
}

export default AnimationManager;
